import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UserPlus, Loader2, Trash2, MonitorCheck } from "lucide-react";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ username: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [assignUser, setAssignUser] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);

  const load = async () => {
    try {
      const [u, d] = await Promise.all([api.get("/users"), api.get("/devices")]);
      setUsers(u.data);
      setDevices(d.data);
    } catch (e) { toast.error("Gagal memuat data"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createMember = async () => {
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success(`Member ${form.username} dibuat`);
      setCreateOpen(false);
      setForm({ username: "", password: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (u, active) => {
    try {
      await api.patch(`/users/${u.id}/status`, { active });
      toast.success(active ? "Diaktifkan" : "Dinonaktifkan");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delUser = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.username} dihapus`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openAssign = async (u) => {
    setAssignUser(u);
    const { data } = await api.get(`/users/${u.id}/devices`);
    setSelectedDevices(data.device_ids);
  };

  const saveAssign = async () => {
    try {
      await api.post(`/users/${assignUser.id}/assign`, { device_ids: selectedDevices });
      toast.success(`Akses PC diperbarui untuk ${assignUser.username}`);
      setAssignUser(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleDevice = (id) =>
    setSelectedDevices((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const allSelected = devices.length > 0 && selectedDevices.length === devices.length;
  const toggleSelectAll = () => setSelectedDevices(allSelected ? [] : devices.map((d) => d.id));

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Manajemen User</h1>
          <p className="text-[#9CA3AF] mt-1 text-sm">Buat member, atur status, dan assign akses per-PC.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="user-create-button" className="bg-[#3B82F6] hover:bg-[#2563EB] rounded-xl h-10">
              <UserPlus size={18} className="mr-1.5" /> Buat Member
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#111827] border-[#1F2937] text-white">
            <DialogHeader><DialogTitle className="font-display">Buat Member Baru</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-[#9CA3AF]">Username</Label>
                <Input data-testid="new-member-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="member01" className="bg-[#0B0F17] border-[#1F2937]" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#9CA3AF]">Password</Label>
                <Input data-testid="new-member-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min. 6 karakter" className="bg-[#0B0F17] border-[#1F2937]" />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-member-button" onClick={createMember} disabled={saving || !form.username || !form.password} className="bg-[#3B82F6] hover:bg-[#2563EB]">
                {saving ? <Loader2 className="animate-spin" size={16} /> : "Simpan"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#3B82F6]" size={28} /></div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-[#111827] border border-[#1F2937] rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-[#1F2937] hover:bg-transparent">
                <TableHead className="text-[#9CA3AF]">Username</TableHead>
                <TableHead className="text-[#9CA3AF]">Role</TableHead>
                <TableHead className="text-[#9CA3AF]">PC Assigned</TableHead>
                <TableHead className="text-[#9CA3AF]">Status</TableHead>
                <TableHead className="text-[#9CA3AF] text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="border-[#1F2937] hover:bg-[#0B0F17]/50">
                  <TableCell className="font-semibold text-white">{u.username}</TableCell>
                  <TableCell>
                    <Badge className={u.role === "master" ? "bg-[#3B82F6]/15 text-[#60A5FA] border-[#3B82F6]/30" : "bg-[#06B6D4]/15 text-[#22D3EE] border-[#06B6D4]/30"}>
                      {u.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-[#9CA3AF]">{u.role === "master" ? "semua" : u.assigned_count}</TableCell>
                  <TableCell>
                    {u.role === "master" ? (
                      <span className="text-[#10B981] text-sm">aktif</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Switch data-testid={`toggle-status-${u.id}`} checked={u.active} onCheckedChange={(v) => toggleActive(u, v)} />
                        <span className={`text-xs ${u.active ? "text-[#10B981]" : "text-[#EF4444]"}`}>{u.active ? "Aktif" : "Nonaktif"}</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {u.role !== "master" && (
                      <div className="flex items-center justify-end gap-2">
                        <Button data-testid={`assign-pc-button-${u.id}`} onClick={() => openAssign(u)} variant="outline" size="sm" className="h-8 bg-transparent border-[#1F2937] text-[#9CA3AF] hover:text-white hover:bg-[#1F2937]">
                          <MonitorCheck size={14} className="mr-1" /> Assign PC
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button data-testid={`delete-user-${u.id}`} variant="ghost" size="icon" className="h-8 w-8 text-[#9CA3AF] hover:text-[#EF4444] hover:bg-[#EF4444]/10"><Trash2 size={15} /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#111827] border-[#1F2937] text-white">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Hapus {u.username}?</AlertDialogTitle>
                              <AlertDialogDescription className="text-[#9CA3AF]">Member dan seluruh assignment-nya akan dihapus.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-[#1F2937] border-[#374151] text-white hover:bg-[#374151]">Batal</AlertDialogCancel>
                              <AlertDialogAction onClick={() => delUser(u)} className="bg-[#EF4444] hover:bg-[#DC2626]">Hapus</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </motion.div>
      )}

      <Dialog open={!!assignUser} onOpenChange={(o) => !o && setAssignUser(null)}>
        <DialogContent className="bg-[#111827] border-[#1F2937] text-white">
          <DialogHeader><DialogTitle className="font-display">Assign PC ke {assignUser?.username}</DialogTitle></DialogHeader>
          {devices.length > 0 && (
            <div className="flex items-center justify-between border-b border-[#1F2937] pb-2">
              <span className="text-xs text-[#6B7280]">{selectedDevices.length} dari {devices.length} PC dipilih</span>
              <Button data-testid="assign-select-all-button" onClick={toggleSelectAll} variant="ghost" size="sm" className="h-7 text-[#60A5FA] hover:text-white hover:bg-[#1F2937]">
                {allSelected ? "Hapus Semua" : "Pilih Semua"}
              </Button>
            </div>
          )}
          <div className="space-y-1 py-2 max-h-80 overflow-auto">
            {devices.length === 0 ? (
              <p className="text-sm text-[#9CA3AF]">Belum ada perangkat.</p>
            ) : devices.map((d) => (
              <label key={d.id} data-testid={`assign-device-option-${d.id}`} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-[#0B0F17] cursor-pointer">
                <Checkbox checked={selectedDevices.includes(d.id)} onCheckedChange={() => toggleDevice(d.id)} className="border-[#374151] data-[state=checked]:bg-[#3B82F6]" />
                <span className="flex-1">{d.name}</span>
                <span className="text-xs text-[#6B7280]">{d.os}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button data-testid="save-assign-button" onClick={saveAssign} className="bg-[#3B82F6] hover:bg-[#2563EB]">Simpan Akses</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
